import React from "react";
import { Panel } from "../../../components/Panel";

interface Column<T> {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  render?: (row: T, rowIndex: number) => React.ReactNode;
  className?: string;
  thClassName?: string;
}

interface ReusableTableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  emptyMessage?: string;

  minHeight?: string | number;
  maxHeight?: string | number;
  tableClassName?: string;
  theadClassName?: string;
  tbodyClassName?: string;
  label: string;
  actions?: React.ReactNode;
  infoTooltip?: string;
  id?: string;
  rowHeight?: number;
  stickyLastRow?: boolean;
  /** Row index to apply dark dashed border. Defaults to last row. Pass -1 to disable. */
  darkBorderRowIndex?: number;
  /** Custom className for Panel wrapper */
  panelClassName?: string;
  /** Custom contentClassName for Panel wrapper */
  panelContentClassName?: string;
}

const ROW_HEIGHT = 50; // px, adjust as needed for your design

export function ReusableTable<T extends { [key: string]: any }>({
  columns,
  data,
  isLoading = false,
  emptyMessage = "No Data Available",

  minHeight = undefined,
  maxHeight = undefined,
  tableClassName = "w-full border-collapse border-spacing-0",
  theadClassName = "border-b border-dashed border-black sticky top-0 bg-surface-primary shadow-sm z-[1]",
  tbodyClassName = "divide-y divide-dashed divide-[#D8D8D8]",
  label,
  actions,
  infoTooltip,
  id,
  rowHeight = ROW_HEIGHT,
  stickyLastRow = false,
  darkBorderRowIndex,
  panelClassName,
  panelContentClassName,
}: ReusableTableProps<T>) {
  const hasData = data.length > 0;
  // Default to last row if not specified, -1 disables it
  const effectiveDarkBorderIndex =
    darkBorderRowIndex === undefined ? data.length - 1 : darkBorderRowIndex;

  // Calculate the number of content rows
  const contentRowCount = hasData ? data.length - 1 : data.length;
  // Calculate row height percentage for even distribution
  const xlRowHeightPercent =
    contentRowCount > 0 ? `${100 / contentRowCount}%` : "auto";

  const renderRow = (row: T, rowIndex: number) => {
    const isFirstRow = rowIndex === 0;
    const isLastRow = rowIndex === data.length - 1;
    const hasDarkBorder = rowIndex === effectiveDarkBorderIndex;

    // If stickyLastRow is true, the last row should be visible with proper height
    // Otherwise, last row gets 0px height (for percentage distribution)
    const shouldHideLastRow = isLastRow && !stickyLastRow;

    const rowStyles = {
      height: shouldHideLastRow ? "0px" : `${rowHeight}px`,
      minHeight: shouldHideLastRow ? "0px" : `${rowHeight}px`,
      maxHeight: shouldHideLastRow ? "0px" : `${rowHeight}px`,
    };

    const cellStyles = {
      height: shouldHideLastRow ? "0px" : `${rowHeight}px`,
      minHeight: shouldHideLastRow ? "0px" : `${rowHeight}px`,
      maxHeight: shouldHideLastRow ? "0px" : `${rowHeight}px`,
    };

    const showDarkBorder = hasDarkBorder;

    return (
      <tr
        key={row.id || rowIndex}
        style={rowStyles}
        className={`
          ${
            stickyLastRow && isLastRow
              ? "sticky bottom-0 bg-surface-primary z-5"
              : ""
          }
          ${showDarkBorder ? "!border-t !border-dashed !border-black " : ""}
        `}
      >
        {columns.map((col, colIndex) => (
          <td
            key={col.key + colIndex}
            className={`py-0 px-2 whitespace-nowrap ${
              col.align === "center"
                ? "text-center"
                : col.align === "right"
                  ? "text-right"
                  : "text-left"
            } ${col.className || ""} ${
              colIndex === 0 && isFirstRow ? "text-content-primary" : ""
            }`}
            style={cellStyles}
          >
            {col.render ? (
              col.render(row, rowIndex)
            ) : (
              <span
                className={`text-xs font-medium leading-none ${
                  isLastRow && darkBorderRowIndex === undefined
                    ? "text-content-primary"
                    : "text-content-tertiary"
                }`}
              >
                {row[col.key]}
              </span>
            )}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <Panel
      label={label}
      headerClassName="!p-1.5 !min-h-[0px]"
      actions={actions}
      className={panelClassName || "h-full min-h-0"}
      contentClassName={
        panelContentClassName ||
        "h-[calc(100%-50px)] xl:flex-1 xl:min-h-0"
      }
      hasBorder={true}
      infoTooltip={infoTooltip}
      id={id}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <div className="!text-brand-primary">Loading...</div>
        </div>
      ) : (
        <div
          className="flex-1 overscroll-none relative overflow-y-auto"
          style={{
            minHeight: minHeight || "300px",
            maxHeight: maxHeight || "400px",
            height: "300px",
          }}
        >
          {/* Main table with data rows */}
          <div>
            <table className={tableClassName}>
              <thead className={theadClassName}>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className={`py-0 px-2 text-xs font-bold text-content-primary whitespace-nowrap ${
                        col.align === "center"
                          ? "text-center"
                          : col.align === "right"
                            ? "text-right"
                            : "text-left"
                      } ${col.thClassName || ""}`}
                      style={{
                        height: `${ROW_HEIGHT}px`,
                        minHeight: `${ROW_HEIGHT}px`,
                        maxHeight: `${ROW_HEIGHT}px`,
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={tbodyClassName}>
                {!hasData ? (
                  <tr className="h-52">
                    <td colSpan={columns.length} className="py-8 text-center">
                      <span className="text-content-tertiary">{emptyMessage}</span>
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => renderRow(row, rowIndex))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  );
}
